import { useState } from 'react';
import api from '@/api';
import {
  X, ChevronRight, ChevronLeft, Check, SkipForward,
  Loader2, CheckCircle2, AlertTriangle
} from 'lucide-react';

const FOLDER_META = [
  { letter: 'A', name: 'Operations & Maintenance Data', desc: 'O&M manuals, equipment data sheets, maintenance schedules' },
  { letter: 'B', name: 'Warranties', desc: 'Contractor warranty letters, manufacturer certificates' },
  { letter: 'C', name: 'As-Builts', desc: 'Record drawings for the trade, marked and signed' },
  { letter: 'D', name: 'Attic Stock / Spare Parts', desc: 'Signed transmittals with quantity, description, location' },
  { letter: 'E', name: 'Training Materials', desc: 'Agendas, sign-in sheets, handouts, recordings' },
  { letter: 'F', name: 'Commissioning / Start-up / Balancing', desc: 'Start-up reports, TAB report, test results' },
  { letter: 'G', name: 'Service Contracts', desc: 'Ongoing service/monitoring agreements' },
  { letter: 'H', name: 'Shop Drawings / Submittals', desc: 'Approved-for-record submittals only' },
  { letter: 'I', name: 'Other', desc: 'Trade-specific certs, licenses, AHJ approvals' },
];

const APPLICABILITY_OPTIONS = [
  { value: 'required', label: 'Required', color: 'bg-blue-600 hover:bg-blue-500', desc: 'Document expected for this trade' },
  { value: 'conditional', label: 'Conditional', color: 'bg-amber-600 hover:bg-amber-500', desc: 'Only if applicable to this scope' },
  { value: 'not_applicable', label: 'N/A', color: 'bg-slate-700 hover:bg-slate-600', desc: 'Not applicable to this trade' },
];

export default function ApplicabilityWizard({ projectId, divisions, onClose, onComplete }) {
  const tradeDivisions = divisions.filter(d => !d.is_project_level && d.applicability !== 'not_applicable');
  const [currentDivIndex, setCurrentDivIndex] = useState(0);
  const [decisions, setDecisions] = useState({});
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('division'); // 'division' | 'folder'

  const currentDiv = tradeDivisions[currentDivIndex];
  const progress = ((currentDivIndex + 1) / tradeDivisions.length) * 100;

  const setDivisionApplicability = (value) => {
    const key = currentDiv.id;
    setDecisions(prev => ({
      ...prev,
      [key]: { division_id: key, applicability: value, reason: '' },
    }));
    // Also set all folders to same value
    for (const f of FOLDER_META) {
      const fKey = `${key}_${f.letter}`;
      setDecisions(prev => ({
        ...prev,
        [fKey]: { division_id: key, folder_letter: f.letter, applicability: value, reason: '' },
      }));
    }
  };

  const setFolderApplicability = (letter, value) => {
    const fKey = `${currentDiv.id}_${letter}`;
    setDecisions(prev => ({
      ...prev,
      [fKey]: { division_id: currentDiv.id, folder_letter: letter, applicability: value, reason: '' },
    }));
  };

  const getDivDecision = () => decisions[currentDiv?.id]?.applicability;
  const getFolderDecision = (letter) => decisions[`${currentDiv?.id}_${letter}`]?.applicability;

  const goNext = () => {
    if (currentDivIndex < tradeDivisions.length - 1) {
      setCurrentDivIndex(currentDivIndex + 1);
      setMode('division');
    }
  };

  const goPrev = () => {
    if (currentDivIndex > 0) {
      setCurrentDivIndex(currentDivIndex - 1);
      setMode('division');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const decisionList = Object.values(decisions);
      await api.post(`/closeout/projects/${projectId}/applicability-wizard`, decisionList);
      onComplete();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const decidedCount = tradeDivisions.filter(d => decisions[d.id]).length;

  if (!tradeDivisions.length) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-sm p-8 max-w-md text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <p className="text-foreground/80">No trade divisions to configure. Import G703 first.</p>
        <button onClick={onClose} className="mt-4 px-4 py-2 bg-secondary text-foreground/80 rounded-sm text-sm">Close</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="applicability-wizard">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-sm w-[700px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold text-foreground">Applicability Wizard</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground/80"><X className="w-5 h-5" /></button>
          </div>
          {/* Progress */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-300 rounded-full" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-xs font-mono text-muted-foreground">{currentDivIndex + 1}/{tradeDivisions.length}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="text-center mb-6">
            <span className="font-mono text-xs text-primary">{currentDiv.csi_number}</span>
            <h3 className="font-heading text-xl font-bold text-foreground mt-1">{currentDiv.trade_name}</h3>
            {currentDiv.vendor_name && <p className="text-sm text-muted-foreground mt-0.5">{currentDiv.vendor_name}</p>}
          </div>

          {mode === 'division' ? (
            <>
              <p className="text-sm text-muted-foreground text-center mb-6">Does this trade require closeout documents?</p>
              <div className="space-y-3">
                {APPLICABILITY_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => setDivisionApplicability(opt.value)}
                    data-testid={`wizard-${opt.value}`}
                    className={`w-full flex items-center gap-4 p-4 rounded-sm border transition-all ${
                      getDivDecision() === opt.value
                        ? 'border-primary/40 bg-orange-950/20'
                        : 'border-border hover:border-border'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-sm ${opt.color} flex items-center justify-center text-white shrink-0 transition-colors`}>
                      {getDivDecision() === opt.value ? <Check className="w-5 h-5" /> : <span className="text-xs font-bold">{opt.value[0].toUpperCase()}</span>}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-foreground">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>

              {getDivDecision() && getDivDecision() !== 'not_applicable' && (
                <button onClick={() => setMode('folder')}
                  className="w-full mt-4 py-2 text-xs text-primary hover:text-primary text-center transition-colors">
                  Customize per folder (optional)
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground text-center mb-4">Fine-tune each folder for <span className="text-foreground font-medium">{currentDiv.trade_name}</span></p>
              <div className="space-y-2">
                {FOLDER_META.map(f => {
                  const decision = getFolderDecision(f.letter) || getDivDecision();
                  return (
                    <div key={f.letter} className="flex items-center gap-3 p-3 bg-card border border-border rounded-sm">
                      <span className="font-mono text-xs text-muted-foreground w-6">{f.letter}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground/80 truncate">{f.name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{f.desc}</p>
                      </div>
                      <div className="flex gap-1">
                        {APPLICABILITY_OPTIONS.map(opt => (
                          <button key={opt.value}
                            onClick={() => setFolderApplicability(f.letter, opt.value)}
                            className={`px-2 py-1 text-[10px] font-mono rounded-sm transition-colors ${
                              decision === opt.value
                                ? opt.value === 'required' ? 'bg-blue-600 text-white'
                                  : opt.value === 'conditional' ? 'bg-amber-600 text-white'
                                  : 'bg-slate-600 text-white'
                                : 'bg-secondary text-muted-foreground hover:text-foreground/80'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button onClick={() => setMode('division')}
                className="w-full mt-3 py-2 text-xs text-muted-foreground hover:text-foreground/80 text-center transition-colors">
                Back to division level
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-border">
          <button onClick={goPrev} disabled={currentDivIndex === 0}
            className="flex items-center gap-1 px-3 py-2 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors">
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{decidedCount}/{tradeDivisions.length} decided</span>
          </div>

          <div className="flex items-center gap-2">
            {currentDivIndex < tradeDivisions.length - 1 ? (
              <>
                <button onClick={goNext}
                  className="flex items-center gap-1 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <SkipForward className="w-3.5 h-3.5" /> Skip
                </button>
                <button onClick={goNext} disabled={!getDivDecision()}
                  data-testid="wizard-next"
                  className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button onClick={handleSave} disabled={saving || decidedCount === 0}
                data-testid="wizard-finish"
                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Apply All ({decidedCount})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
